import type { SpotAccuracy } from "@/lib/conditions/forecast-accuracy";

type Props = {
  accuracy: SpotAccuracy | null;
  spotName: string;
};

function thresholdStyles(pct: number) {
  if (pct >= 75) return { bg: "bg-emerald-50", border: "border-emerald-100", text: "text-emerald-700" };
  if (pct >= 50) return { bg: "bg-amber-50", border: "border-amber-100", text: "text-amber-700" };
  return { bg: "bg-red-50", border: "border-red-100", text: "text-red-700" };
}

function trendLabel(trend: SpotAccuracy["trend"]) {
  switch (trend) {
    case "improving":
      return { arrow: "↑", label: "Improving" };
    case "declining":
      return { arrow: "↓", label: "Declining" };
    case "stable":
      return { arrow: "→", label: "Stable" };
    default:
      return null;
  }
}

export function ForecastAccuracy({ accuracy, spotName }: Props) {
  if (!accuracy || accuracy.totalReports < 5) {
    return null;
  }

  const styles = thresholdStyles(accuracy.accuracyPct);
  const trend = trendLabel(accuracy.trend);

  return (
    <div className={`${styles.bg} ${styles.border} rounded-[16px] border p-5 md:p-7`}>
      <h3 className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted mb-3">
        Forecast accuracy at {spotName}
      </h3>

      <div className="flex items-baseline gap-3 mb-1">
        <span className={`font-serif text-[36px] md:text-[44px] font-bold leading-none ${styles.text}`}>
          {accuracy.accuracyPct}%
        </span>
        {trend && (
          <span className={`text-[14px] font-medium ${styles.text}`}>
            {trend.arrow} {trend.label}
          </span>
        )}
      </div>

      <p className="text-[13px] text-text-muted mb-3">
        Based on {accuracy.totalReports} verified forecast check{accuracy.totalReports !== 1 ? "s" : ""}
      </p>

      <p className="text-[12px] text-text-muted/70">
        {accuracy.spotOnCount} spot on, {accuracy.closeCount} close, {accuracy.wayOffCount} way off
      </p>
    </div>
  );
}
